import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

export const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav className="bg-white shadow-lg relative z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="https://www.bhaktisarovar.in"
            className="flex items-center py-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <img
              src="https://www.bhaktisarovar.in/assets/images/logo.png"
              alt="Bhakti Sarovar Logo"
              className="h-12 w-auto object-contain"
            />
          </motion.a>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <NavLink to="/" active={location.pathname === '/'}>
              Home
            </NavLink>
            <NavLink to="https://www.bhaktisarovar.in/greetings/" active={location.pathname === 'https://www.bhaktisarovar.in/greetings/'}>
              Greetings
            </NavLink>
            <NavLink to="/travel-direction" active={location.pathname === '/travel-direction'}>
              Travel Disha Shool
            </NavLink>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
            whileTap={{ scale: 0.95 }}
            onClick={toggleMenu}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="absolute top-full left-0 right-0 bg-white shadow-lg md:hidden"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="px-4 py-3 space-y-2">
              <MobileNavLink 
                to="/"
                active={location.pathname === '/'}
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </MobileNavLink>
              <MobileNavLink 
                to="https://www.bhaktisarovar.in/greetings/"
                active={location.pathname === '/greetings'}
                onClick={() => setIsMenuOpen(false)}
              >
                Greetings
              </MobileNavLink>
              <MobileNavLink 
                to="/travel-direction"
                active={location.pathname === '/travel-direction'}
                onClick={() => setIsMenuOpen(false)}
              >
                Travel Disha Shool
              </MobileNavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Helper component for desktop navigation links
const NavLink = ({ 
  to,
  children, 
  active = false 
}: { 
  to: string;
  children: React.ReactNode; 
  active?: boolean;
}) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    <Link
    to={to}
    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
      active
        ? 'text-purple-600 bg-purple-50'
        : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
    }`}
  >
    {children}
  </Link>
  </motion.div>
);

// Helper component for mobile navigation links
const MobileNavLink = ({ 
  to,
  children, 
  active = false,
  onClick
}: { 
  to: string;
  children: React.ReactNode; 
  active?: boolean;
  onClick?: () => void;
}) => (
  <motion.div
    whileTap={{ scale: 0.95 }}
  >
    <Link
    to={to}
    className={`block px-3 py-2 rounded-md text-base font-medium ${
      active
        ? 'text-purple-600 bg-purple-50'
        : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
    }`}
    onClick={onClick}
  >
    {children}
  </Link>
  </motion.div>
);